import {
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import { useServiceError } from '@/hooks';

import {
  affectPrice,
  companyConsumableStockPrices,
  companyPartStockPrices,
  listConsumableStockPrices,
  listPartStockPrices,
  listStockItems,
} from './StockItems.service';
import {
  ListStockItemsQuery,
  PartStockPrices,
  StockItem,
} from './StockItems.types';

import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import Authorities from '@/types/Authority';
import { notification } from 'antd';
import notificationError from '@/utils/functions/notificationError';
import { PageResultStock } from '@/core/request/PageResultStock';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { queryClient } from '@/Root';

export function useStockItems(
  enabled: boolean,
  params?: ListStockItemsQuery,
  refreshToken = false,
): UseInfiniteQueryResult<PageResultStock<StockItem>> {
  const query = useInfiniteQuery<PageResultStock<StockItem>>(
    ['stockItems', { ...params }],
    ({ pageParam }) => {
      const req: ListStockItemsQuery = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listStockItems(req);
    },
    {
      refetchOnWindowFocus: refreshToken,
      enabled: enabled && hasPermissions([Authorities.REGISTER_PART_VIEW]),
      getNextPageParam: (lastPage: PageResultStock<StockItem>) =>
        lastPage.result.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useStockItemsMutation() {
  return useMutation(
    (params?: ListStockItemsQuery) => {
      if (hasPermissions([Authorities.REGISTER_PART_VIEW])) {
        const req: ListStockItemsQuery = {
          order: params?.order || DEFAULT_ORDER,
          rows: params?.rows || DEFAULT_ROWS,
          ...params,
        };

        return listStockItems(req);
      }

      return Promise.resolve(undefined);
    },
    {
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function usePartStockPrices(partId?: number, enabled?: boolean) {
  const cache = queryClient.getQueryData(['partStockPrices', partId]);

  return useQuery(
    ['partStockPrices', partId],
    () => listPartStockPrices(partId),
    {
      enabled: enabled && !cache && !!partId,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function usePartStockPricesMutation() {
  const query = useMutation({
    mutationFn: (partId?: number) => {
      const cache = queryClient.getQueryData<PartStockPrices>([
        'partStockPrices',
        partId,
      ]);

      if (cache) return Promise.resolve(cache);

      return listPartStockPrices(partId);
    },
    onSuccess(data, partId) {
      queryClient.setQueryData(['partStockPrices', partId], data);
    },
  });

  useServiceError(query.error);

  return query;
}

export function useCompanyPartStockPrices() {
  const queryChanel = useQueryChanel();

  const mutation = useMutation(companyPartStockPrices, {
    onSuccess(_, vars) {
      queryChanel({
        invalidateQueries: [['workorder/kit/detail']],
        removeQueries: [['partStockPrices', vars.partId]],
      });
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useConsumableStockPrices(partId: number) {
  const query = useQuery(['consumableStockPrices', partId], () =>
    listConsumableStockPrices(partId),
  );

  useServiceError(query.error);

  return query;
}

export function useCompanyConsumableStockPrices() {
  const queryClient = useQueryClient();

  const mutation = useMutation(companyConsumableStockPrices, {
    onSuccess() {
      queryClient.invalidateQueries(['consumableStockPrices']);
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useAffectPrice() {
  const queryClient = useQueryClient();

  const mutation = useMutation(affectPrice, {
    async onSuccess() {
      await queryClient.invalidateQueries('stockItems');

      notification.success({
        message: 'Sucesso',
        description: 'Preços alterados com sucesso!',
      });
    },
    onError(error) {
      notificationError(error);
    },
  });

  return mutation;
}
